
import { EditorRange } from "./Range";

/**
 * @typedef {Object} SerializedPosition
 * @property {number[]} path
 * @property {number} offset
 */

/**
 * @typedef {Object} SerializedRange
 * @property {SerializedPosition} start
 * @property {SerializedPosition} end
 */

export class EditorSelection {
  constructor(editor) {
    this.editor = editor;
  }

  // =========================
  // BASIC
  // =========================

  getRange() {
    const range = EditorRange.fromSelection();
    if (!range || !range.native) return null;

    const root = this.editor.root;
    if (!root.contains(range.native.commonAncestorContainer)) {
      return null;
    }

    return range;
  }

  isCollapsed() {
    const range = this.getRange();
    return range?.isCollapsed() ?? true;
  }

  collapseToEnd() {
    const range = this.getRange();
    if (!range) return;

    range.collapse(false);
    range.select();
  }

  // Set selection at the end of editor content
  setAtEnd() {
    const root = this.editor.root;
    const range = document.createRange();
    const selection = window.getSelection();

    if (!selection) return;

    // Find the last text node in the editor
    const findLastTextNode = (node) => {
      // If it's a text node, return it
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        return node;
      }

      // Traverse children backwards to find last text node
      for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const result = findLastTextNode(node.childNodes[i]);
        if (result) return result;
      }

      return null;
    };

    const lastTextNode = findLastTextNode(root);

    if (lastTextNode) {
      // Set cursor at end of last text node
      const length = lastTextNode.textContent?.length || 0;
      range.setStart(lastTextNode, length);
      range.setEnd(lastTextNode, length);
    } else {
      // If no text node found, set at end of root
      range.selectNodeContents(root);
      range.collapse(false);
    }

    selection.removeAllRanges();
    selection.addRange(range);
  }

  // =========================
  // SERIALIZATION (IMPORTANT)
  // =========================

  save() {
    const range = this.getRange();
    if (!range || !range.native) return null;

    return {
      start: this.serializePosition(
        range.native.startContainer,
        range.native.startOffset,
      ),
      end: this.serializePosition(
        range.native.endContainer,
        range.native.endOffset,
      ),
    };
  }

  restore(data) {
    if (!data) {
      console.log('RESTORE SELECTION: No data, setting cursor at end');
      this.setAtEnd();
      return;
    }

    const start = this.deserializePosition(data.start);
    const end = this.deserializePosition(data.end);

    if (!start || !end) {
      console.warn('RESTORE SELECTION: Invalid position data, setting cursor at end', {
        startPath: data.start?.path,
        endPath: data.end?.path,
        startFound: !!start,
        endFound: !!end
      });
      this.setAtEnd();
      return;
    }

    try {
      const range = document.createRange();

      // Validate nodes are still in the document
      if (!this.editor.root.contains(start.node) || !this.editor.root.contains(end.node)) {
        console.warn('RESTORE SELECTION: Nodes not in document, setting cursor at end', {
          startInDoc: this.editor.root.contains(start.node),
          endInDoc: this.editor.root.contains(end.node),
          startPath: data.start?.path,
          endPath: data.end?.path
        });
        this.setAtEnd();
        return;
      }

      // Validate and clamp start offset
      const startOffset = this.clampOffset(start.node, start.offset);
      range.setStart(start.node, startOffset);

      // Validate and clamp end offset
      const endOffset = this.clampOffset(end.node, end.offset);
      range.setEnd(end.node, endOffset);

      const selection = window.getSelection();
      if (!selection) {
        this.setAtEnd();
        return;
      }

      selection.removeAllRanges();
      selection.addRange(range);

      console.log('RESTORE SELECTION: Success', {
        startPath: data.start?.path,
        endPath: data.end?.path,
        startOffset,
        endOffset
      });
    } catch (e) {
      console.warn('RESTORE SELECTION: Exception, setting cursor at end', {
        error: e.message,
        startPath: data.start?.path,
        endPath: data.end?.path
      });
      this.setAtEnd();
      return;
    }
  }

  clampOffset(node, offset) {
    if (node.nodeType === Node.TEXT_NODE) {
      // For text nodes, clamp to text length
      const maxOffset = node.textContent?.length || 0;
      return Math.min(offset, maxOffset);
    } else {
      // For element nodes, clamp to number of children
      const maxOffset = node.childNodes.length;
      return Math.min(offset, maxOffset);
    }
  }

  serializePosition(node, offset) {
    return {
      path: this.getNodePath(node),
      offset,
    };
  }

  deserializePosition(pos) {
    const node = this.getNodeFromPath(pos.path);
    if (!node) return null;
    return { node, offset: pos.offset };
  }

  getNodePath(node) {
    const path = [];
    let current = node;

    while (current && current !== this.editor.root) {
      const parent = current.parentNode;
      if (!parent) break;

      const index = Array.from(parent.childNodes).indexOf(current);
      path.unshift(index);
      current = parent;
    }

    return path;
  }

  getNodeFromPath(path) {
    let current = this.editor.root;

    for (let i = 0; i < path.length; i++) {
      const index = path[i];

      if (!current.childNodes[index]) {
        console.warn(`Path index ${index} not found at level ${i}`, {
          path,
          availableChildren: current.childNodes.length,
          currentNode: current
        });

        // Fallback: try to find closest valid node
        if (current.childNodes.length > 0) {
          // Use the last child if index is out of bounds
          const fallbackIndex = Math.min(index, current.childNodes.length - 1);
          console.log(`Using fallback index ${fallbackIndex}`);
          current = current.childNodes[fallbackIndex];

          // If we found a text node, return it
          if (current.nodeType === Node.TEXT_NODE) {
            return current;
          }

          // If it's an element, try to find the last text node in it
          const lastTextNode = this.findLastTextNode(current);
          if (lastTextNode) {
            return lastTextNode;
          }

          return current;
        }

        // If no children available, return current
        return current;
      }

      current = current.childNodes[index];
    }

    return current;
  }

  findLastTextNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node;
    }

    // Traverse children backwards to find last text node
    for (let i = node.childNodes.length - 1; i >= 0; i--) {
      const textNode = this.findLastTextNode(node.childNodes[i]);
      if (textNode) return textNode;
    }

    return null;
  }

  findFirstTextNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node;
    }

    for (let child of node.childNodes) {
      const textNode = this.findFirstTextNode(child);
      if (textNode) return textNode;
    }

    return null;
  }

  // =========================
  // BLOCK LEVEL HELPERS
  // =========================

  getAnchorBlock() {
    const range = this.getRange();
    if (!range || !range.native) return null;

    return this.getBlockAncestor(range.native.startContainer);
  }

  getFocusBlock() {
    const range = this.getRange();
    if (!range || !range.native) return null;

    return this.getBlockAncestor(range.native.endContainer);
  }

  hasNodeInContext({ nodes, check }) {
    const checkName = check.toLowerCase();

    const hasMatch = (node) => {
      // 1. self
      if (node.nodeName.toLowerCase() === checkName) return true;

      // 2. parent
      let current = node.parentNode;
      while (current) {
        if (current.nodeName.toLowerCase() === checkName) return true;
        current = current.parentNode;
      }

      // 3. children DFS
      const dfs = (n) => {
        if (!n.childNodes?.length) return false;

        for (const child of n.childNodes) {
          if (child.nodeName.toLowerCase() === checkName) return true;
          if (dfs(child)) return true;
        }

        return false;
      };

      return dfs(node);
    };

    // normalize to array
    const list =
      nodes instanceof NodeList
        ? Array.from(nodes)
        : Array.isArray(nodes)
          ? nodes
          : [nodes];

    return list.some(hasMatch);
  }

  getBlockAncestor(node) {
    let current = node;

    while (current && current !== this.editor.root) {
      if (current instanceof HTMLElement && this.isBlockElement(current)) {
        return current;
      }
      current = current.parentNode;
    }

    return null;
  }

  isBlockElement(element) {
    const blockTags = [
      "P",
      "DIV",
      "H1",
      "H2",
      "H3",
      "H4",
      "H5",
      "H6",
      "BLOCKQUOTE",
      "UL",
      "OL",
      "LI",
    ];
    return blockTags.includes(element.tagName);
  }

  getDeepTextNode(node) {
    while (node && node.firstChild) {
      node = node.firstChild;
    }
    return node;
  }

  getNodePosition(node, offset) {
    const root = this.editor.root;
    if (!node || !root) return null;
    if (!root.contains(node)) return null;

    let targetNode = node;
    let targetOffset = offset;

    // 🔥 Nếu là ELEMENT_NODE
    if (node.nodeType === Node.ELEMENT_NODE) {
      const childCount = node.childNodes.length;

      // Cursor ở đầu element
      if (offset === 0) {
        const firstChild = node.childNodes[0];
        if (!firstChild) {
          // element rỗng
          return { path: this.getNodePath(node), offset: 0 };
        }

        targetNode = this.getDeepTextNode(firstChild);
        targetOffset = 0;
      }

      // Cursor ở cuối element
      else if (offset >= childCount) {
        const lastChild = node.childNodes[childCount - 1];
        targetNode = this.getDeepTextNode(lastChild);
        targetOffset = targetNode.textContent.length;
      }

      // Cursor ở giữa element
      else {
        const prevChild = node.childNodes[offset - 1];
        targetNode = this.getDeepTextNode(prevChild);
        targetOffset = targetNode.textContent.length;
      }
    }

    // 🔥 Nếu là TEXT_NODE giữ nguyên offset
    const path = [];
    let current = targetNode;

    while (current && current !== root) {
      const parent = current.parentNode;
      if (!parent) break;

      const index = Array.prototype.indexOf.call(parent.childNodes, current);
      if (index === -1) break;

      path.unshift(index);
      current = parent;
    }

    return {
      path,
      offset: targetOffset,
    };
  }

  getModelSelection(selection) {
    if (!selection || selection.rangeCount === 0) return null;
    return {
      anchor: this.getNodePosition(
        selection.anchorNode,
        selection.anchorOffset
      ),
      focus: this.getNodePosition(
        selection.focusNode,
        selection.focusOffset
      ),
    };
  }

  comparePath(p1, p2) {
    const len = Math.min(p1.length, p2.length);

    for (let i = 0; i < len; i++) {
      if (p1[i] < p2[i]) return -1;
      if (p1[i] > p2[i]) return 1;
    }

    if (p1.length < p2.length) return -1;
    if (p1.length > p2.length) return 1;

    return 0;
  }


  getSelectedBlocks() {
    const range = this.getRange();
    if (!range || !range.native) return [];

    const startBlock = this.getBlockAncestor(range.native.startContainer);
    const endBlock = this.getBlockAncestor(range.native.endContainer);

    if (!startBlock) return [];
    if (!endBlock) return startBlock ? [startBlock] : [];

    // Nếu cùng 1 block
    if (startBlock === endBlock) return [startBlock];

    // Collect tất cả block elements trong editor
    const allBlocks = Array.from(
      this.editor.root.querySelectorAll(
        "p, div, h1, h2, h3, h4, h5, h6, blockquote, li",
      ),
    );

    // Tìm index của start và end block
    const startIndex = allBlocks.indexOf(startBlock);
    const endIndex = allBlocks.indexOf(endBlock);

    if (startIndex === -1 || endIndex === -1) return [startBlock];

    // 🔥 Nếu focus ở offset 0 của endBlock, không include endBlock
    const endOffset = range.native.endOffset;
    const isEndAtStart = endOffset === 0 && range.native.endContainer === endBlock;

    const finalEndIndex = isEndAtStart ? endIndex - 1 : endIndex;

    // Nếu finalEndIndex < startIndex, chỉ return startBlock
    if (finalEndIndex < startIndex) return [startBlock];

    // Lấy tất cả blocks từ start đến end
    return allBlocks.slice(startIndex, finalEndIndex + 1);
  }

  getSelectedNodes(selection) {
    const root = this.editor.root;
    if (!selection || !root) return [];

    let { anchor, focus } = selection;

    // normalize
    if (this.comparePath(anchor.path, focus.path) > 0) {
      [anchor, focus] = [focus, anchor];
    }

    const textNodes = [];
    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT
    );

    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    const getPath = (node) => {
      const path = [];
      let current = node;

      while (current && current !== root) {
        const parent = current.parentNode;
        const index = Array.prototype.indexOf.call(parent.childNodes, current);
        path.unshift(index);
        current = parent;
      }

      return path;
    };

    const result = [];

    for (const textNode of textNodes) {
      const path = getPath(textNode);
      const cmpStart = this.comparePath(path, anchor.path);
      const cmpEnd = this.comparePath(path, focus.path);

      // 🔥 node nằm giữa hoàn toàn
      if (cmpStart > 0 && cmpEnd < 0) {
        result.push({
          node: textNode,
          startOffset: 0,
          endOffset: textNode.textContent.length
        });
      }

      // 🔥 node start
      if (cmpStart === 0) {
        result.push({
          node: textNode,
          startOffset: anchor.offset,
          endOffset:
            this.comparePath(anchor.path, focus.path) === 0
              ? focus.offset
              : textNode.textContent.length
        });
      }

      // 🔥 node end (chỉ khi offset > 0)
      if (cmpEnd === 0 && focus.offset > 0) {
        result.push({
          node: textNode,
          startOffset: 0,
          endOffset: focus.offset
        });
      }
    }

    return result;
  }
}
